import { useMemo, useState } from 'react';
import { Head } from '@inertiajs/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, Upload, Calendar, CheckCircle2, XCircle } from 'lucide-react';
import { PlatformConsoleBreadcrumb } from '@/components/platform_console/PlatformConsoleBreadcrumb';
import DestinationCheckboxes, {
  submittedDestinations,
  type DestinationSelection,
} from '@/components/documents/DestinationCheckboxes';
import MunicipalityLinkFields, {
  municipalitiesForDocumentType,
  municipalityLabel,
  type MunicipalityLinkSelection,
  type MunicipalityOption,
} from '@/components/documents/MunicipalityLinkFields';
import { type UploadConfig, uploadConfigFor } from '@/lib/fileLibraryUploadConfig';

type FileStatus = 'queued' | 'uploading' | 'success' | 'error';

type Row = {
  file: File;
  name: string;
  meetingDate?: string;
  status: FileStatus;
  error?: string;
};

type DocumentsMassUploadProps = {
  municipalities: MunicipalityOption[];
  stateOptions: string[];
  fileLibraryUpload: UploadConfig;
};

function deriveNameFromFilename(filename: string): string {
  const withoutExt = filename.replace(/\.[^/.]+$/, '');
  return withoutExt.replace(/[\-_]+/g, ' ').replace(/\s+/g, ' ').trim();
}

function getCsrfToken(): string {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
}

type MassUploadDocumentType = 'hierarchical' | 'meeting_minutes' | 'current_state_law' | 'potential_state_law' | 'uploaded_file';

export default function DocumentsMassUpload({ municipalities, stateOptions, fileLibraryUpload }: DocumentsMassUploadProps) {
  const [municipalityOptions, setMunicipalityOptions] = useState(municipalities);
  const [rows, setRows] = useState<Row[]>([]);
  const [documentType, setDocumentType] = useState<MassUploadDocumentType>('hierarchical');
  const [stateCode, setStateCode] = useState('');
  const [updateCadence, setUpdateCadence] = useState('');
  const [links, setLinks] = useState<MunicipalityLinkSelection[]>([]);
  const [destinations, setDestinations] = useState<DestinationSelection>(
    submittedDestinations(),
  );
  const [folderDrafts, setFolderDrafts] = useState<Record<number, string>>({});
  const [creatingFolderForId, setCreatingFolderForId] = useState<number | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [globalError, setGlobalError] = useState<string | null>(null);

  const total = rows.length;
  const uploadedCount = rows.filter(r => r.status === 'success').length;
  const remainingCount = Math.max(0, total - uploadedCount);
  const progressValue = total === 0 ? 0 : Math.round((uploadedCount / total) * 100);

  const requiresDates = documentType === 'meeting_minutes';
  const requiresState = documentType === 'current_state_law' || documentType === 'potential_state_law';
  const fileUploadConfig = uploadConfigFor(documentType, fileLibraryUpload);
  const allowedMunicipalities = municipalitiesForDocumentType(municipalityOptions, documentType);
  const activeLinks = allowedMunicipalities
    ? links.filter((link) => allowedMunicipalities.some((municipality) => municipality.id === link.municipalityId))
    : [];

  const canStart = useMemo(() => {
    if (isUploading) return false;
    if (!total) return false;
    if (!documentType) return false;
    if (requiresState && !stateCode) return false;
    if (!updateCadence) return false;
    if (requiresDates) {
      return rows.every(r => !!r.meetingDate);
    }
    return true;
  }, [isUploading, total, documentType, requiresState, stateCode, updateCadence, requiresDates, rows]);

  const onFilesSelected = (files: FileList | null) => {
    if (!files) return;
    const fileArray = Array.from(files);
    const newRows: Row[] = fileArray.map((file) => ({
      file,
      name: deriveNameFromFilename(file.name),
      status: 'queued'
    }));
    setRows(newRows);
  };

  const updateRow = (index: number, update: Partial<Row>) => {
    setRows(prev => {
      const next = [...prev];
      next[index] = { ...next[index], ...update };
      return next;
    });
  };

  const startUpload = async () => {
    setGlobalError(null);
    setIsUploading(true);
    try {
      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        if (row.status === 'success') continue;
        updateRow(i, { status: 'uploading', error: undefined });

        const form = new FormData();
        form.append('name', row.name);
        form.append('file', row.file);
        form.append('document_type', documentType);
        form.append('update_cadence', updateCadence);
        if (requiresState) {
          form.append('state_abbreviation', stateCode);
        }
        activeLinks.forEach((link) => {
          form.append('municipality_ids[]', String(link.municipalityId));
          if (link.folderId) {
            form.append(`municipality_document_group_ids[${link.municipalityId}]`, link.folderId);
          }
        });
        if (requiresDates && row.meetingDate) {
          form.append('meeting_date', row.meetingDate);
        }
        form.append('file_center', destinations.file_center ? '1' : '0');
        form.append('connect', destinations.connect ? '1' : '0');
        form.append('research_assistant', destinations.research_assistant ? '1' : '0');

        try {
          const response = await fetch('/documents.json', {
            method: 'POST',
            headers: {
              'X-CSRF-Token': getCsrfToken(),
              'X-Requested-With': 'XMLHttpRequest',
              'Accept': 'application/json'
            },
            body: form
          });

          if (!response.ok) {
            const data = await response.json().catch(() => ({}));
            const message = data?.errors ? JSON.stringify(data.errors) : `HTTP ${response.status}`;
            updateRow(i, { status: 'error', error: message });
          } else {
            updateRow(i, { status: 'success', error: undefined });
          }
        } catch (e: any) {
          updateRow(i, { status: 'error', error: e?.message || 'Network error' });
        }
      }
    } finally {
      setIsUploading(false);
    }
  };

  const createFolder = async (municipalityId: number) => {
    const municipality = municipalityOptions.find((option) => option.id === municipalityId);
    const folderName = folderDrafts[municipalityId]?.trim() ?? '';
    if (!municipality || !folderName || creatingFolderForId !== null) return;

    setGlobalError(null);
    setCreatingFolderForId(municipalityId);

    const rootGroups = municipality.municipality_document_groups.filter((group) => group.parent_id == null);
    const nextPosition =
      rootGroups.reduce((maxPosition, group) => Math.max(maxPosition, group.position), -1) + 1;

    try {
      const response = await fetch(`/municipalities/${municipality.id}/document_groups.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCsrfToken(),
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          municipality_document_group: {
            name: folderName,
            description: '',
            position: nextPosition,
            parent_id: null,
          }
        })
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        const message = Array.isArray(data?.errors) ? data.errors.join(', ') : `HTTP ${response.status}`;
        setGlobalError(message);
        return;
      }

      const createdGroup = data.document_group as MunicipalityOption['municipality_document_groups'][number] | undefined;
      if (!createdGroup) {
        setGlobalError('Folder was created but the response was incomplete.');
        return;
      }

      setMunicipalityOptions((currentMunicipalities) =>
        currentMunicipalities.map((option) =>
          option.id === municipality.id
            ? {
                ...option,
                municipality_document_groups: [...option.municipality_document_groups, createdGroup].sort(
                  (left, right) => left.position - right.position
                )
              }
            : option
        )
      );
      setLinks((current) => current.map((link) => (
        link.municipalityId === municipality.id ? { ...link, folderId: String(createdGroup.id) } : link
      )));
      setFolderDrafts((current) => ({ ...current, [municipality.id]: '' }));
    } catch (error: any) {
      setGlobalError(error?.message || 'Failed to create folder');
    } finally {
      setCreatingFolderForId(null);
    }
  };

  return (
    <>
      <Head title="Mass Upload Documents" />
      <div className="container mx-auto p-6 max-w-6xl">
        <div className="space-y-6">
          <PlatformConsoleBreadcrumb section="document_pipeline" current="Mass Upload" />
          <h1 className="text-3xl font-bold">Mass Upload Documents</h1>

        {globalError && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{globalError}</AlertDescription>
          </Alert>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Upload Settings</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-2">
                <Label htmlFor="document_type">Document Type</Label>
                <select
                  id="document_type"
                  value={documentType}
                  onChange={(e) => {
                    const nextType = e.target.value as MassUploadDocumentType;
                    const allowed = municipalitiesForDocumentType(municipalityOptions, nextType);
                    setDocumentType(nextType);
                    setDestinations(submittedDestinations());
                    setLinks((current) =>
                      current.filter((link) =>
                        (allowed ?? []).some((municipality) => municipality.id === link.municipalityId),
                      ),
                    );
                  }}
                  className="border rounded-md p-2 w-full"
                >
                  <option value="hierarchical">Hierarchical (Ordinances, Codes, Standards)</option>
                  <option value="meeting_minutes">Meeting Minutes</option>
                  <option value="current_state_law">Current State Law</option>
                  <option value="potential_state_law">Potential State Law</option>
                  <option value="uploaded_file">Uploaded file</option>
                </select>
              </div>

              {requiresState && (
                <div className="space-y-2">
                  <Label htmlFor="state_abbreviation">State Abbreviation</Label>
                  <select
                    id="state_abbreviation"
                    value={stateCode}
                    onChange={(e) => setStateCode(e.target.value)}
                    className="border rounded-md p-2 w-full"
                  >
                    <option value="">Select state</option>
                    {stateOptions.map((state) => (
                      <option key={state} value={state}>{state}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="update_cadence">Update cadence / expiration</Label>
                <select
                  id="update_cadence"
                  value={updateCadence}
                  onChange={(e) => setUpdateCadence(e.target.value)}
                  className="border rounded-md p-2 w-full"
                >
                  <option value="">Select cadence</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="yearly">Yearly</option>
                </select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="files">Files</Label>
                <Input
                  id="files"
                  type="file"
                  multiple
                  accept={fileUploadConfig.accept}
                  onChange={(e) => onFilesSelected(e.target.files)}
                />
                <p className="text-xs text-muted-foreground">{fileUploadConfig.help}</p>
              </div>

            </div>
            <DestinationCheckboxes
              documentType={documentType}
              value={destinations}
              onChange={setDestinations}
            />
            <MunicipalityLinkFields
              municipalities={municipalityOptions}
              documentType={documentType}
              links={activeLinks}
              onChange={setLinks}
              renderFolderActions={(municipality) => (
                <div className="flex gap-2">
                  <Input
                    value={folderDrafts[municipality.id] ?? ''}
                    onChange={(event) => setFolderDrafts((current) => ({
                      ...current,
                      [municipality.id]: event.target.value,
                    }))}
                    placeholder="Create a new folder"
                    disabled={creatingFolderForId !== null}
                    aria-label={`New folder for ${municipality.name}`}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => createFolder(municipality.id)}
                    disabled={creatingFolderForId !== null || !(folderDrafts[municipality.id] ?? '').trim()}
                  >
                    {creatingFolderForId === municipality.id ? 'Creating…' : 'New Folder'}
                  </Button>
                </div>
              )}
            />
            {activeLinks.length > 0 && (
              <p className="text-sm text-gray-600">
                Uploaded documents will be associated with {activeLinks.map((link) => {
                  const municipality = municipalityOptions.find((option) => option.id === link.municipalityId);
                  return municipality ? municipalityLabel(municipality) : null;
                }).filter(Boolean).join(', ')}.
              </p>
            )}
          </CardContent>
        </Card>

        {rows.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Batch</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Uploaded {uploadedCount} of {total} • Remaining {remainingCount}
                </div>
                <div className="w-64">
                  <Progress value={progressValue} className="h-2" />
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-blue-500 text-white">
                      <th className="text-left px-3 py-2">Filename</th>
                      <th className="text-left px-3 py-2">Name</th>
                      {requiresDates && (
                        <th className="text-left px-3 py-2">Meeting Date</th>
                      )}
                      <th className="text-left px-3 py-2">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, idx) => (
                      <tr key={idx} className="border-b">
                        <td className="px-3 py-2 align-middle">{row.file.name}</td>
                        <td className="px-3 py-2 align-middle">
                          <Input
                            value={row.name}
                            onChange={(e) => updateRow(idx, { name: e.target.value })}
                          />
                        </td>
                        {requiresDates && (
                          <td className="px-3 py-2 align-middle">
                            <div className="flex items-center gap-2">
                              <Calendar className="h-4 w-4 text-gray-500" />
                              <Input
                                type="date"
                                value={row.meetingDate || ''}
                                onChange={(e) => updateRow(idx, { meetingDate: e.target.value })}
                              />
                            </div>
                          </td>
                        )}
                        <td className="px-3 py-2 align-middle">
                          {row.status === 'queued' && (
                            <span className="text-gray-500">Queued</span>
                          )}
                          {row.status === 'uploading' && (
                            <span className="text-blue-600 flex items-center gap-1"><Upload className="h-4 w-4" /> Uploading</span>
                          )}
                          {row.status === 'success' && (
                            <span className="text-green-600 flex items-center gap-1"><CheckCircle2 className="h-4 w-4" /> Uploaded</span>
                          )}
                          {row.status === 'error' && (
                            <span className="text-red-600 flex items-center gap-1"><XCircle className="h-4 w-4" /> Failed</span>
                          )}
                          {row.error && (
                            <div className="text-red-600 text-xs mt-1 break-all">{row.error}</div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {requiresDates && !rows.every(r => !!r.meetingDate) && (
                <div className="text-xs text-red-600">Meeting date is required for all files when uploading meeting minutes.</div>
              )}

              <div className="flex justify-end">
                <Button onClick={startUpload} disabled={!canStart} className="px-6">
                  {isUploading ? 'Uploading…' : 'Start Upload'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
        </div>
      </div>
    </>
  );
}
